import { useEffect, useRef } from "react";

const MAX_PARTICLES = 46;
const PARTICLE_DENSITY = 28000;
const LINE_COUNT = 4;

// --- Pure helpers (no closures over component state) -------------------------

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const createParticles = (count, width, height) =>
  Array.from({ length: count }).map(() => ({
    x: Math.random() * width,
    y: Math.random() * height,
    r: Math.random() * 1.4 + 0.4,
    vx: (Math.random() - 0.5) * 0.18,
    vy: (Math.random() - 0.5) * 0.18,
    a: Math.random() * 0.45 + 0.15,
  }));

const createLines = (count) =>
  Array.from({ length: count }).map((_, i) => ({
    seed: i * 1.7,
    offset: 0,
  }));

const wrapParticle = (p, width, height) => {
  if (p.x < -10) p.x = width + 10;
  if (p.x > width + 10) p.x = -10;
  if (p.y < -10) p.y = height + 10;
  if (p.y > height + 10) p.y = -10;
};

const drawArc = (ctx, line, width, height) => {
  ctx.beginPath();
  const baseY = height * (0.35 + 0.12 * Math.sin(line.seed));
  const amp = 60 + 30 * Math.cos(line.seed * 1.3);
  ctx.moveTo(-50, baseY);
  for (let x = -50; x <= width + 50; x += 14) {
    const y =
      baseY +
      Math.sin((x + line.offset) * 0.012 + line.seed) * amp +
      Math.sin((x + line.offset) * 0.04 + line.seed * 2) * 6;
    ctx.lineTo(x, y);
  }
  ctx.strokeStyle = "rgba(57, 255, 20, 0.10)";
  ctx.lineWidth = 1;
  ctx.stroke();
};

const drawParticle = (ctx, p) => {
  ctx.beginPath();
  ctx.fillStyle = `rgba(234, 240, 246, ${p.a})`;
  ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
  ctx.fill();
};

// --- Component ---------------------------------------------------------------

// Lightweight canvas particles + drifting map arc lines, paused when tab hidden.
export const Particles = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = prefersReducedMotion();

    // Mutable state for this effect (sized at runtime, mutated by tick)
    const state = {
      raf: 0,
      particles: [],
      lines: [],
      width: 0,
      height: 0,
      visible: true,
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      state.width = canvas.clientWidth;
      state.height = canvas.clientHeight;
      canvas.width = Math.floor(state.width * dpr);
      canvas.height = Math.floor(state.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const init = () => {
      resize();
      const count = Math.min(
        MAX_PARTICLES,
        Math.floor((state.width * state.height) / PARTICLE_DENSITY)
      );
      state.particles = createParticles(count, state.width, state.height);
      state.lines = createLines(LINE_COUNT);
    };

    const tick = () => {
      if (!state.visible) {
        state.raf = requestAnimationFrame(tick);
        return;
      }
      ctx.clearRect(0, 0, state.width, state.height);

      state.lines.forEach((line) => {
        line.offset += reduced ? 0 : 0.3;
        drawArc(ctx, line, state.width, state.height);
      });

      state.particles.forEach((p) => {
        if (!reduced) {
          p.x += p.vx;
          p.y += p.vy;
          wrapParticle(p, state.width, state.height);
        }
        drawParticle(ctx, p);
      });

      state.raf = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      state.visible = !document.hidden;
    };

    init();
    state.raf = requestAnimationFrame(tick);
    window.addEventListener("resize", init);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelAnimationFrame(state.raf);
      window.removeEventListener("resize", init);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="hero-particles-canvas"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
};
